import { useState, type CSSProperties, type FormEvent } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AuthLayout, authFormStyles as s } from '@/components/auth/AuthLayout'
import { CodeInput } from '@/components/auth/CodeInput'
import { authApi } from '@/api/authApi'
import { ApiError } from '@/api/httpClient'
import { useCountdown } from '@/hooks/useCountdown'
import { useAuthStore } from '@/stores/authStore'
import { morphToast } from '@/components/ui/Toast'

const CODE_LENGTH = 6

function numberFrom(error: unknown, field: string): number | null {
  if (!(error instanceof ApiError)) return null
  const raw = error.raw as Record<string, unknown> | null
  const value = raw?.[field]
  return typeof value === 'number' ? value : null
}

export default function VerifyEmailPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const user = useAuthStore((st) => st.user)
  const verifyEmail = useAuthStore((st) => st.verifyEmail)
  const logout = useAuthStore((st) => st.logout)

  const [code, setCode] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [resending, setResending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { seconds, start, isCounting } = useCountdown(0)

  // The code that brought the user here was sent at signup, so this screen only
  // offers to resend — it never fires one off on mount (that would double every
  // reload and burn the cooldown).
  if (user?.email_verified) return <Navigate to="/" replace />

  function handleCodeChange(next: string) {
    setCode(next)
    // Cleared on typing, not on every `code` change: the failure handler resets the
    // boxes, and an effect would wipe the message the user still needs to read.
    if (error) setError(null)
  }

  async function submit(submittedCode: string) {
    if (submittedCode.length !== CODE_LENGTH || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      await verifyEmail(submittedCode)
      morphToast.success(t('auth.verifyEmail.confirmed', 'E-mail confirmado!'))
      navigate('/', { replace: true })
    } catch (err) {
      if (err instanceof ApiError && err.code === 'EMAIL_ALREADY_VERIFIED') {
        navigate('/', { replace: true })
        return
      }
      const remaining = numberFrom(err, 'remaining_attempts')
      const base = err instanceof Error ? err.message : String(err)
      setError(
        remaining !== null && remaining > 0
          ? t('auth.verifyEmail.attemptsLeft', {
              defaultValue: '{message} Você ainda tem {attempts} tentativa(s).',
              message: base,
              attempts: remaining,
            })
          : base,
      )
      setCode('')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    await submit(code)
  }

  /**
   * Endereco errado nao se conserta no perfil: a conta pendente ficaria
   * ocupando o e-mail digitado por engano. Derruba a sessao e devolve o
   * usuario ao cadastro — a API reaproveita o registro nao confirmado, entao
   * ate o mesmo endereco pode ser usado de novo.
   */
  async function handleChangeEmail() {
    await logout()
    navigate('/register', { replace: true })
  }

  async function handleResend() {
    if (isCounting || resending) return
    setResending(true)
    setError(null)
    try {
      const response = await authApi.sendEmailCode()
      setCode('')
      start(response.resend_in)
      morphToast.success(t('auth.verifyEmail.resent', 'Enviamos um novo código.'))
    } catch (err) {
      const retryAfter = numberFrom(err, 'retry_after')
      if (retryAfter !== null) start(retryAfter)
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setResending(false)
    }
  }

  return (
    <AuthLayout
      title={t('auth.verifyEmail.title', 'Confirme seu e-mail')}
      subtitle={t('auth.verifyEmail.subtitle', {
        defaultValue: 'Enviamos um código de 6 dígitos para {email}.',
        email: user?.email ?? '',
      })}
      footer={
        <span>
          {t('auth.verifyEmail.wrongEmail', 'E-mail errado?')}{' '}
          <button type="button" onClick={() => void handleChangeEmail()} style={linkButton}>
            {t('auth.verifyEmail.changeEmail', 'Alterar e-mail')}
          </button>
          {' · '}
          <button type="button" onClick={() => void logout()} style={linkButton}>
            {t('auth.verifyEmail.logout', 'Sair')}
          </button>
        </span>
      }
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {error ? <div style={s.error}>{error}</div> : null}

        <CodeInput
          value={code}
          onChange={handleCodeChange}
          onComplete={(completed) => void submit(completed)}
          length={CODE_LENGTH}
          disabled={submitting}
          invalid={Boolean(error)}
          autoFocus
          label={t('auth.verifyEmail.codeLabel', 'Código de confirmação')}
        />

        <button
          type="submit"
          style={{ ...s.button, opacity: code.length === CODE_LENGTH && !submitting ? 1 : 0.65 }}
          disabled={submitting || code.length !== CODE_LENGTH}
        >
          {submitting
            ? t('auth.verifyEmail.submitting', 'Confirmando...')
            : t('auth.verifyEmail.submit', 'Confirmar e-mail')}
        </button>

        <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>
          {t('auth.verifyEmail.noCode', 'Não recebeu o código?')}{' '}
          {isCounting ? (
            <span>
              {t('auth.verifyEmail.resendIn', {
                defaultValue: 'Reenviar em {seconds}s',
                seconds,
              })}
            </span>
          ) : (
            <button type="button" onClick={() => void handleResend()} style={linkButton} disabled={resending}>
              {resending
                ? t('auth.verifyEmail.resending', 'Enviando...')
                : t('auth.verifyEmail.resend', 'Reenviar código')}
            </button>
          )}
        </div>
      </form>
    </AuthLayout>
  )
}

const linkButton: CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  font: 'inherit',
  color: 'var(--accent)',
  cursor: 'pointer',
}
